<template>
  <div class="fellows-page">
    <!-- Search Section -->
    <section class="search-section py-4 bg-light">
      <div class="container">
        <div class="row">
          <div class="col-md-12">
            <div class="row">
              <div class="col-md-3">
                <!-- Search Input -->
                <div class="search-input-container flex-fill">
                  <i class="bi bi-search search-icon"></i>
                  <input
                    type="text"
                    class="form-control search-input"
                    placeholder="Type Name here"
                    v-model="searchQuery"
                    @input="performSearch"
                  >
                </div>
              </div>
              <div class="col-md-3">
                <!-- Location Filter -->
                <div class="filter-container">
                  <i class="bi bi-geo-alt filter-icon"></i>
                  <select class="form-select filter-select" v-model="selectedLocation"
                          @change="onLocationFilterChange">
                    <option value="">All locations</option>
                    <option v-for="country in availableCountries" :key="country" :value="country">
                      {{ country }}
                    </option>
                  </select>
                </div>
              </div>
              <div class="col-md-3">
                <!-- Fellowship Type Filter -->
                <div class="filter-container">
                  <i class="bi bi-person filter-icon"></i>
                  <select class="form-select filter-select" v-model="selectedFellowshipType" @change="applyFilters">
                    <option value="">All Fellowship Types</option>
                    <option v-for="(label, id) in FELLOWSHIP_TYPE_MAP" :key="id" :value="id">
                      {{ label }}
                    </option>
                  </select>
                </div>
              </div>
              <div class="col-md-3">
                <!-- Disease Area Filter -->
                <div class="filter-container">
                  <i class="bi bi-person filter-icon"></i>
                  <select
                    class="form-select filter-select"
                    v-model="selectedWorkingGroup"
                    @change="applyFilters"
                  >
                    <option value="">Research Area</option>

                    <option
                      v-for="group in researchAreaOptions"
                      :key="group.id"
                      :value="String(group.id)"
                    >
                      {{ group.name }}
                    </option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Results Section -->
    <section class="results-section py-5" style="background: #E6F5F7">
      <div class="container">
        <!-- Loading State -->
        <div v-if="loading" class="text-center py-5">
          <div class="spinner-border text-primary mb-3" style="width: 3rem; height: 3rem;"></div>
          <p class="text-muted">Loading fellows data...</p>
        </div>

        <!-- Error State -->
        <div v-if="error" class="alert alert-danger alert-dismissible fade show mb-4" role="alert">
          <i class="bi bi-exclamation-triangle me-2"></i>
          {{ error }}
          <button type="button" class="btn-close" @click="error = null"></button>
        </div>

        <div class="row" v-show="!loading">
        <!-- Map Column -->
          <div class="col-lg-8 mb-4">
            <div class="map-container">
              <div class="map-header d-flex justify-content-between align-items-center mb-3">
                <h5 class="mb-0">Fellows Distribution</h5>
              </div>
              <div id="africa-map" style="height: 500px;"></div>
            </div>
          </div>

          <!-- Fellows List Column -->
          <div class="col-lg-4">
            <div class="fellows-list-container">
              <!-- Results Header -->
              <div class="results-header d-flex justify-content-between align-items-center mb-4">
                <div class="results-count">
                  <span class="count-label">We found</span>
                  <span class="count-number">{{ filteredFellows.length }}</span>
                  <span class="count-text">fellows</span>
                </div>
                <div class="sort-container">
                  <label class="sort-label">Sort by</label>
                  <select class="form-select sort-select" v-model="sortBy" @change="sortFellows">
                    <option value="updated">Last updated</option>
                    <option value="name">Name</option>
                    <option value="year">Year</option>
                    <option value="country">Country</option>
                  </select>
                </div>
              </div>

              <!-- Fellows List -->
              <div class="fellows-list">
                <!-- Compact Fellow Cards -->
                <div
                  v-for="fellow in displayedFellows"
                  :key="fellow.id"
                  class="fellow-card-compact"
                  :class="{ 'highlighted': isCountrySelected(fellow.country) }"
                >
                  <!-- Card Header -->
                  <div class="fellow-header-compact row">
                    <div class="fellow-avatar-compact col-md-2" style="margin: auto;">
                      <img
                        :src="getUserAvatar(fellow)"
                        :alt="fellow.full_name || fellow.name"
                        class="avatar-img-compact"
                        @error="onAvatarError($event, fellow)"
                      >
                      <div class="status-badge-compact"></div>
                    </div>
                    <div class="fellow-info-compact col-md-10">
                      <h4 class="fellow-name-compact" style="color: rgb(37 81 83);font-weight: 700;letter-spacing: .2px;font-size: 1.25rem;">{{ fellow.full_name }}</h4>
                      <div class="fellow-meta-compact">
                        <span class="fellow-type-compact" style="color: rgb(249 162 26);font-size: 1rem;line-height: 1.5rem;letter-spacing: .16px; font-size: 1rem;"><b>{{
                            fellow.fellow_details?.current_job_title || 'Fellow'
                          }}</b></span>
                      </div>
                      <div class="detail-compact" v-if="fellow.fellow_details?.current_organisation">
                        <span class="detail-text-compact">{{
                            fellow.fellow_details.current_organisation
                          }}</span>
                      </div>
                    </div>
                  </div>
                  <br>
                  <!-- Action Button -->
                  <button class="btn view-btn-compact" @click="openFellowModal(fellow)">
                    View Profile <i class="bi bi-arrow-right"></i>
                  </button>
                </div>

                <!-- No Results -->
                <div v-if="filteredFellows.length === 0 && !loading"
                     class="text-center py-5 text-muted">
                  <i class="bi bi-search" style="font-size: 2rem;"></i>
                  <p class="mt-2">No fellows found matching your criteria</p>
                  <button class="btn btn-outline-primary" @click="clearFilters">
                    Clear Filters
                  </button>
                </div>

                <!-- Load More Button -->
                <div class="text-center mt-4" v-if="hasMoreFellows">
                  <button class="btn btn-outline-primary" @click="loadMoreFellows">
                    Load More Fellows
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <FellowPreviewDialog
      v-model="isFellowPreviewOpen"
      :fellow="selectedFellow"
      :avatar-resolver="getUserAvatar"
      :avatar-error-handler="onAvatarError"
    />
  </div>
</template>

<script>
import UserService from '@/services/users/UserService'
import FellowPreviewDialog from '@/components/common/FellowPreviewDialog.vue'

const FELLOWSHIP_TYPE_MAP = {
  1: "Career Development Fellowship (CDF)",
  2: "Senior Fellowship (SF)",
  3: "EDCTP Clinical Research & Development Fellowship (R&D F)",
  4: "EDCTP-AREF Preparatory Fellowships (PF)",
  5: "Senior Fellowship Plus (SFP)",
  6: "Disease Outbreak and Epidemic Response (EPI Fellows)",
  7: "Female PhD Fellowships",
  8: "EDCTP Master's Fellowship"
};

const DISEASE_AREA_MAP = {
  1: 'Human Immuno-deficiency Virus (HIV)',
  2: 'Tuberculosis (TB)',
  3: 'Malaria',
  4: 'Neglected Infectious Diseases (NIDs)',
  5: 'Emerging Infections (EIs)',
  6: 'Diarrhoeal Diseases (DDs)',
  7: 'Lower Respiratory Tract Infections (LRTIs)',
  8: 'Cross Cutting',
  9: 'Epi Fellows',
  10: 'PSIA Fellows'
};


export default {
  name: 'OurFellows',
  components: {
    FellowPreviewDialog
  },

  watch: {
    '$route.query.openFellow': {
      handler(newValue) {
        if (!newValue || !this.allFellows.length) return
        this.openFellowFromQuery(newValue)
      }
    },
    isFellowPreviewOpen(isOpen) {
      document.body.style.overflow = isOpen ? 'hidden' : 'auto'
      if (!isOpen) {
        this.selectedFellow = null
      }
    }
  },

  data() {
    return {
      loading: false,
      error: null,
      searchQuery: '',
      selectedLocation: '',
      selectedFellowship: '',
      sortBy: 'updated',
      displayLimit: 6,
      selectedCountryOnMap: null,
      mapInitialized: false,
      mapInstance: null,
      geoJsonLayer: null,
      countryMarkers: [],
      allFellows: [],
      fellowCountryData: {},
      selectedFellow: null,
      isFellowPreviewOpen: false,
      mapInteractionLocked: false,
      DISEASE_AREA_MAP,
      countryAliasMap: {
        // South Africa
        'ZA': 'South Africa',

        // East Africa
        'UG': 'Uganda',
        'KE': 'Kenya',
        'TZ': 'Tanzania',
        'TZAR': 'Tanzania',
        'RW': 'Rwanda',
        'BI': 'Burundi',
        'ET': 'Ethiopia',
        'SO': 'Somalia',
        'DJ': 'Djibouti',
        'ER': 'Eritrea',

        // Central Africa
        'CD': 'Democratic Republic of the Congo',
        'CG': 'Republic of the Congo',
        'CF': 'Central African Republic',
        'CENTRAL-AFRICAN-REPUBLIC': 'Central African Republic',
        'GA': 'Gabon',
        'GQ': 'Equatorial Guinea',
        'CM': 'Cameroon',
        'TD': 'Chad',

        // West Africa
        'NG': 'Nigeria',
        'GH': 'Ghana',
        'SL': 'Sierra Leone',
        'LR': 'Liberia',
        'CI': "Côte d'Ivoire",
        'IVORY COAST': "Côte d'Ivoire",
        'BF': 'Burkina Faso',
        'TG': 'Togo',
        'BJ': 'Benin',
        'ML': 'Mali',
        'SN': 'Senegal',
        'GN': 'Guinea',
        'GW': 'Guinea-Bissau',
        'GM': 'Gambia',
        'NE': 'Niger',
        'MR': 'Mauritania',

        // Southern Africa
        'ZW': 'Zimbabwe',
        'ZM': 'Zambia',
        'MW': 'Malawi',
        'BW': 'Botswana',
        'NA': 'Namibia',
        'MZ': 'Mozambique',
        'SZ': 'Eswatini',
        'SWAZILAND': 'Eswatini',
        'LS': 'Lesotho',
        'AO': 'Angola',
        'CV': 'Cape Verde',

        // Indian Ocean
        'MG': 'Madagascar',
        'MU': 'Mauritius',
        'KM': 'Comoros',
        'SC': 'Seychelles',

        // North Africa
        'SD': 'Sudan',
        'SS': 'South Sudan',
        'TN': 'Tunisia',
        'DZ': 'Algeria',
        'MA': 'Morocco',
        'LY': 'Libya',
        'EG': 'Egypt'
      },
      selectedFellowshipType: "",
      selectedWorkingGroup: "",
      FELLOWSHIP_TYPE_MAP,
      workingGroups: [],
    }
  },

  computed: {
    availableCountries() {
      const countries = this.allFellows
        .map(f => this.resolveCountryName(f.country))
        .filter(Boolean);

      return [...new Set(countries)].sort();
    },

    researchAreaOptions() {
      if (!Array.isArray(this.workingGroups)) return []

      return this.workingGroups
        .filter(group => group && group.id != null && group.name)
        .map(group => ({ id: group.id, name: group.name }))
        .sort((a, b) => a.name.localeCompare(b.name))
    },

    filteredFellows() {
      let fellows = [...this.allFellows]

      // Text search
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase()
        fellows = fellows.filter(fellow =>
          fellow.full_name?.toLowerCase().includes(query) ||
          fellow.country?.toLowerCase().includes(query)
        )
      }

      // Dropdown filter — normalize both sides
      if (this.selectedLocation) {
        fellows = fellows.filter(f =>
          this.resolveCountryName(f.country) === this.selectedLocation
        );
      }


      // Filter by fellowship type (number)
      if (this.selectedFellowshipType) {
        fellows = fellows.filter(f => {
          const details = f.fellowship_details;

          // If empty or missing array → skip
          if (!Array.isArray(details) || details.length === 0) {
            return false;
          }

          // Check if ANY fellowship record matches the selected type
          return details.some(d => {
            const callNumber = d.edctp_project_call;
            return Number(callNumber) === Number(this.selectedFellowshipType);
          });
        });
      }

      // Filter by Research Area aligned to working groups
      if (this.selectedWorkingGroup) {
        fellows = fellows.filter(f => {
          const workingGroupIds = this.getFellowWorkingGroupIds(f)
          return workingGroupIds.includes(String(this.selectedWorkingGroup))
        });
      }

      // Map click filter — normalize both sides
      if (this.selectedCountryOnMap) {
        fellows = fellows.filter(fellow =>
          this.resolveCountryName(fellow.country || '').toLowerCase() ===
          this.resolveCountryName(this.selectedCountryOnMap).toLowerCase()
        )
      }

      return this.sortFellowsList(fellows)
    },

    displayedFellows() {
      return this.filteredFellows.slice(0, this.displayLimit)
    },

    hasMoreFellows() {
      return this.filteredFellows.length > this.displayLimit
    }
  },

  async mounted() {
    this.loading = true;

    await this.loadWorkingGroups(); // must load first
    await this.loadFellows();       // normalization happens here
    this.generateCountryData();      // only now is it valid

    this.$nextTick(() => {
      this.initializeMap();

      // ensure labels load AFTER map + country data exist
      setTimeout(() => {
        if (this.mapInstance) {
          this.addCountryLabels();
          this.mapInstance.invalidateSize();
        }
      }, 300);
    });

    this.openFellowFromQuery(this.$route.query.openFellow)

    this.loading = false;
  },

  beforeUnmount() {
    if (this.mapInstance) {
      this.mapInstance.remove()
    }
    document.body.style.overflow = 'auto'
  },

  methods: {
    openFellowFromQuery(fellowId) {
      const numericId = Number(fellowId)

      if (!Number.isFinite(numericId)) return

      const match = this.allFellows.find(fellow => Number(fellow.id) === numericId)

      if (match) {
        this.openFellowModal(match)
      }
    },

    onLocationFilterChange() {
      this.displayLimit = 6;

      // Reset map click selection and regenerate marker counts from active filters.
      this.selectedCountryOnMap = null;
      this.generateCountryData(this.filteredFellows);

      // If "All Locations" is selected (empty value)
      if (!this.selectedLocation) {

        // Reset everything related to filtering
        // Restore all markers
        this.addCountryLabels();

        // Reset polygon highlight
        if (this.geoJsonLayer) {
          this.geoJsonLayer.eachLayer(layer => {
            layer.setStyle(this.getCountryStyle(layer.feature));
          });
        }

        // Reset map zoom to Africa view
        if (this.mapInstance) {
          this.mapInstance.setView([0, 20], 3);
        }

        return;
      }

      // Otherwise handle a real country selection
      const resolvedName = this.resolveCountryName(this.selectedLocation);

      this.selectedCountryOnMap = resolvedName;

      // Highlight the selected country
      if (this.geoJsonLayer) {
        this.geoJsonLayer.eachLayer(layer => {
          layer.setStyle(this.getCountryStyle(layer.feature));
        });
      }

      // Update map markers to show only selected one
      this.updateMarkersForSelectedCountry(resolvedName);
    },

    async loadWorkingGroups() {
      try {
        const response = await (await import('@/services/workinggroups/WorkingGroupServices')).default.getWorkingGroups();

        // API returns either { data: [...] } or { Data: { data: [...] } }
        if (response?.Data?.data) {
          this.workingGroups = response.Data.data;
        } else if (response?.data?.data) {
          this.workingGroups = response.data.data;
        } else if (response?.data) {
          this.workingGroups = response.data;
        } else {
          this.workingGroups = [];
        }

      } catch (error) {
        console.error("Error loading working groups:", error);
        this.workingGroups = [];
      }
    },

    getFellowWorkingGroupIds(fellow) {
      const idSet = new Set()

      const addValues = (value) => {
        if (value == null) return

        if (Array.isArray(value)) {
          value.forEach(item => addValues(item))
          return
        }

        if (typeof value === 'object') {
          addValues(value.id)
          return
        }

        if (typeof value === 'string' && value.includes(',')) {
          value
            .split(',')
            .map(item => item.trim())
            .filter(Boolean)
            .forEach(item => idSet.add(String(item)))
          return
        }

        const normalized = String(value).trim()
        if (normalized) {
          idSet.add(normalized)
        }
      }

      // API returns working groups as snake_case array from working_group_members pivot
      addValues(fellow?.working_groups)

      // fellow_details (hasOne) stores selected working group IDs as JSON/comma-separated string
      addValues(fellow?.fellow_details?.working_group)

      return [...idSet]
    },

    updateMarkersForSelectedCountry(countryName) {
      if (!this.mapInstance) return;

      // stop leaflet animations from referencing deleted layers
      this.mapInstance.stop();

      // Remove old markers safely
      this.countryMarkers.forEach(marker => {
        if (marker && this.mapInstance.hasLayer(marker)) {
          this.mapInstance.removeLayer(marker);
        }
      });
      this.countryMarkers = [];

      const count = this.fellowCountryData[countryName] || 0;
      if (!count) return;

      const coords = this.getCountryCoordinates(countryName);
      if (!coords) return;

      const icon = L.divIcon({
        className: 'my-div-icon',
        html: `<div style="
      background: white;
      border: 2px solid #009CAD;
      border-radius: 50% 50% 50% 0;
      width: 30px;
      height: 30px;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 3px 8px rgba(0,0,0,0.3);
      transform: rotate(-45deg);
    ">
      <div style="transform: rotate(45deg); font-weight:bold; color:#009CAD;">
        ${count}
      </div>
    </div>`,
        iconSize: [30, 30],
        iconAnchor: [10, 35]
      });

      const marker = L.marker(coords, { icon }).addTo(this.mapInstance);
      this.countryMarkers.push(marker);

      // Safe recenter (flyTo sometimes causes race condition)
      this.mapInstance.setView(coords, 5, { animate: false });
    },

    getPublications(fellow) {
      if (!fellow || !fellow.fellow_details) return []

      let publications = fellow.fellow_details.publications

      if (typeof publications === 'string') {
        try {
          publications = JSON.parse(publications)
        } catch (e) {
          return []
        }
      }

      if (!Array.isArray(publications)) return []

      return publications
    },

    togglePublication(index) {
      const currentIndex = this.expandedPublications.indexOf(index)
      if (currentIndex > -1) {
        this.expandedPublications.splice(currentIndex, 1)
      } else {
        this.expandedPublications.push(index)
      }
    },

    isValidAuthor(author) {
      if (!author || typeof author.name !== 'string') return false
      const name = author.name.trim().toLowerCase()
      const unknownPatterns = ['unknown', 'author unknown', 'unknown author', 'n/a', 'none', 'anonymous', '']
      return !unknownPatterns.includes(name)
    },

    getValidAuthors(publication) {
      if (!publication.authors || !Array.isArray(publication.authors)) return []
      return publication.authors.filter(author => this.isValidAuthor(author))
    },

    toggleAuthors(pubIndex) {
      const currentIndex = this.expandedAuthors.indexOf(pubIndex)
      if (currentIndex > -1) {
        this.expandedAuthors.splice(currentIndex, 1)
      } else {
        this.expandedAuthors.push(pubIndex)
      }
    },

    formatPublicationDate(dateString) {
      if (!dateString) return 'No date'

      try {
        const date = new Date(dateString)
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })
      } catch (e) {
        return dateString
      }
    },

    formatProjectDuration(fellowshipDetails) {
      if (!fellowshipDetails) return 'Not provided'

      const startDate = fellowshipDetails.edctp_project_start_date
      const endDate = fellowshipDetails.edctp_project_end_date

      if (!startDate && !endDate) {
        return 'Not provided'
      }

      const formatDate = (dateString) => {
        if (!dateString) return null
        try {
          const date = new Date(dateString)
          return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          })
        } catch (e) {
          return dateString
        }
      }

      const formattedStart = formatDate(startDate)
      const formattedEnd = formatDate(endDate)

      if (formattedStart && formattedEnd) {
        return `${formattedStart} - ${formattedEnd}`
      } else if (formattedStart) {
        return `From ${formattedStart}`
      } else if (formattedEnd) {
        return `Until ${formattedEnd}`
      }

      return 'Not provided'
    },

    getSites(fellowshipDetails) {
      if (!fellowshipDetails || !fellowshipDetails.sites) return []

      let sites = fellowshipDetails.sites

      if (typeof sites === 'string') {
        try {
          sites = JSON.parse(sites)
        } catch (e) {
          return []
        }
      }

      if (!Array.isArray(sites)) return []

      return sites.filter(site => site && site.trim())
    },

    getCollaborators(fellowshipDetails) {
      if (!fellowshipDetails || !fellowshipDetails.collaborators) return []

      let collaborators = fellowshipDetails.collaborators

      if (typeof collaborators === 'string') {
        try {
          collaborators = JSON.parse(collaborators)
        } catch (e) {
          return []
        }
      }

      if (!Array.isArray(collaborators)) return []

      // If collaborators are objects with institution property
      if (collaborators.length > 0 && typeof collaborators[0] === 'object') {
        return collaborators.map(c => c.institution || c.name).filter(Boolean)
      }

      // If collaborators are strings
      return collaborators.filter(c => c && c.trim())
    },

    formatStatus(status) {
      if (!status) return 'Not specified'
      return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
    },

    getStatusClass(status) {
      if (!status) return 'status-default'

      switch (status.toLowerCase()) {
        case 'active':
          return 'status-active'
        case 'completed':
          return 'status-completed'
        case 'suspended':
          return 'status-suspended'
        case 'pending':
          return 'status-pending'
        default:
          return 'status-default'
      }
    },

    formatGender(gender) {
      if (!gender) return 'Not specified'
      return gender.charAt(0).toUpperCase() + gender.slice(1).toLowerCase()
    },

    // Helper method for case-insensitive country comparison
    isCountrySelected(country) {
      if (!this.selectedCountryOnMap || !country) return false
      return country.toLowerCase() === this.selectedCountryOnMap.toLowerCase()
    },

    generateCountryData(sourceFellows = this.allFellows) {
      const data = {};

      sourceFellows.forEach(f => {
        if (!f.country) return;

        const resolved = this.resolveCountryName(f.country);
        if (!resolved) return;

        data[resolved] = (data[resolved] || 0) + 1;
      });

      this.fellowCountryData = data;
    },

    resolveCountryName(name) {
      if (!name) return null;

      const formatted = name.toString().trim().toUpperCase();

      // 1️⃣ Exact alias / ISO match
      if (this.countryAliasMap[formatted]) {
        return this.countryAliasMap[formatted];
      }

      // 2️⃣ Try normalised versions (GeoJSON names)
      const normalised = formatted
        .replace(/[^A-Z ]/g, '')
        .replace(/\s+/g, ' ')
        .trim();

      if (this.countryAliasMap[normalised]) {
        return this.countryAliasMap[normalised];
      }

      // 3️⃣ Assume already a valid country name
      return name;
    },

    getUserAvatar(user) {
      if (!user) return this.generateFallbackAvatar('User')

      const avatarSources = [user.avatar_url, user.avatar]

      for (const avatar of avatarSources) {
        if (avatar && typeof avatar === 'string' && avatar.trim()) {
          const trimmedAvatar = avatar.trim()
          if (trimmedAvatar.startsWith('http://') || trimmedAvatar.startsWith('https://')) {
            return trimmedAvatar
          }
          if (trimmedAvatar.startsWith('/')) {
            return `${window.location.origin}${trimmedAvatar}`
          }
          if (trimmedAvatar.startsWith('uploads/') || trimmedAvatar.startsWith('storage/')) {
            return `${window.location.origin}/${trimmedAvatar}`
          }
        }
      }

      const name = this.getUserDisplayName(user)
      return this.generateFallbackAvatar(name)
    },

    getUserDisplayName(user) {
      if (user.full_name && user.full_name.trim()) return user.full_name.trim()
      if (user.name && user.name.trim()) return user.name.trim()
      const firstName = user.first_name ? user.first_name.trim() : ''
      const lastName = user.last_name ? user.last_name.trim() : ''
      if (firstName || lastName) return `${firstName} ${lastName}`.trim()
      return 'User'
    },

    generateFallbackAvatar(name) {
      const cleanName = name || 'User'
      const initials = cleanName.split(' ').map(word => word.charAt(0).toUpperCase()).join('').slice(0, 2)
      const colors = ['1a8090', '2c3e50', '8e44ad', '27ae60', 'f39c12', 'e74c3c', '3498db', 'e67e22', '9b59b6', '1abc9c']
      let hash = 0
      for (let i = 0; i < cleanName.length; i++) {
        hash = cleanName.charCodeAt(i) + ((hash << 5) - hash)
      }
      const colorIndex = Math.abs(hash) % colors.length
      const backgroundColor = colors[colorIndex]
      return `https://ui-avatars.com/api/?name=${encodeURIComponent(initials)}&size=120&background=${backgroundColor}&color=fff&bold=true&format=png`
    },

    onAvatarError(event, user) {
      if (!event || !event.target) return
      if (event.target.src && event.target.src.includes('ui-avatars.com')) {
        event.target.onerror = null
        return
      }
      const name = this.getUserDisplayName(user)
      event.target.src = this.generateFallbackAvatar(name)
      event.target.onerror = null
    },

    async initializeMap() {
      if (this.mapInitialized || this.loading) return

      try {
        if (!window.L) await this.loadLeaflet()

        const mapContainer = document.getElementById('africa-map')
        if (!mapContainer) return

        mapContainer.innerHTML = ''

        const africaBounds = L.latLngBounds(
          L.latLng(-35, -25),
          L.latLng(37, 55)
        )

        // --- CREATE MAP FIRST ---
        this.mapInstance = L.map('africa-map', {
          center: [0, 20],
          zoom: 3,
          minZoom: 3,
          maxZoom: 6,
          maxBounds: africaBounds,
          zoomControl: true,
          dragging: true,
          scrollWheelZoom: false,
          doubleClickZoom: false,
          attributionControl: false
        })

        // --- WAIT FOR MAP TO BE READY ---
        this.mapInstance.whenReady(async () => {
          // Load geojson AFTER map is mounted
          await this.loadAfricaGeoJSON()

          // Add labels AFTER layers exist
          this.addCountryLabels()

          // Fit map boundaries AFTER data + layer render
          this.mapInstance.fitBounds(africaBounds, {
            padding: [20, 20],
            maxZoom: 3.5,
            animate: false
          })

          // Prevent Leaflet resizing bug
          setTimeout(() => {
            this.mapInstance.invalidateSize()
          }, 250)

          // Only mark ready when everything is in place
          this.mapInitialized = true
        })

      } catch (error) {
        console.error('Error initializing map:', error)
        this.showMapError()
      }
    },

    async loadLeaflet() {
      return new Promise((resolve, reject) => {
        if (window.L) {
          this.injectSafeFlyTo();
          resolve();
          return;
        }

        const cssLink = document.createElement('link');
        cssLink.rel = 'stylesheet';
        cssLink.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
        document.head.appendChild(cssLink);

        const script = document.createElement('script');
        script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
        script.onload = () => {
          this.injectSafeFlyTo(); // <-- now L exists
          resolve();
        };
        script.onerror = () => reject(new Error('Failed to load Leaflet'));
        document.head.appendChild(script);
      });
    },

    injectSafeFlyTo() {
      if (!window.L) return;

      // Disable all popup animation to prevent _latLngToNewLayerPoint crash
      L.Map.mergeOptions({
        closePopupOnClick: false,
        zoomAnimation: false,
        markerZoomAnimation: false,
      });

      // Patch popup animation function to remove the offending method
      L.Popup.prototype._animateZoom = function () {
        // No animation = no referring to destroyed marker position
      };

      // Safe flyTo override
      if (!L.Map.prototype.safeFlyTo) {
        L.Map.prototype.safeFlyTo = function (coords, zoom = 5) {
          if (!this._loaded) return;
          this.stop();
          return this.setView(coords, zoom, { animate: false });
        };
      }
    },

    async loadAfricaGeoJSON() {
      try {
        const response = await fetch('https://raw.githubusercontent.com/datasets/geo-countries/master/data/countries.geojson')
        const worldData = await response.json()

        const africaCountries = [
          'Algeria', 'Angola', 'Benin', 'Botswana', 'Burkina Faso', 'Burundi',
          'Cameroon', 'Cape Verde', 'Central African Republic', 'Chad', 'Comoros',
          'Democratic Republic of the Congo', 'Republic of the Congo', 'Djibouti', 'Egypt',
          'Equatorial Guinea', 'Eritrea', 'Eswatini', 'Ethiopia', 'Gabon', 'Gambia', 'Ghana',
          'Guinea', 'Guinea-Bissau', 'Ivory Coast', 'Côte d\'Ivoire', 'Kenya', 'Lesotho',
          'Liberia', 'Libya', 'Madagascar', 'Malawi', 'Mali', 'Mauritania', 'Mauritius',
          'Morocco', 'Mozambique', 'Namibia', 'Niger', 'Nigeria', 'Rwanda',
          'São Tomé and Príncipe', 'Sao Tome and Principe', 'Senegal', 'Seychelles',
          'Sierra Leone', 'Somalia', 'South Africa', 'South Sudan', 'Sudan',
          'Tanzania', 'United Republic of Tanzania', 'Togo', 'Tunisia', 'Uganda',
          'Zambia', 'Zimbabwe'
        ]

        const africaData = {
          type: 'FeatureCollection',
          features: worldData.features.filter(feature => {
            const countryName = this.getCountryName(feature)
            return africaCountries.some(africanCountry =>
              countryName.toLowerCase() === africanCountry.toLowerCase()
            )
          })
        }

        this.geoJsonLayer = L.geoJSON(africaData, {
          style: (feature) => this.getCountryStyle(feature),
          onEachFeature: (feature, layer) => this.onEachFeature(feature, layer)
        }).addTo(this.mapInstance)

      } catch (error) {
        console.error('Error loading Africa GeoJSON:', error)
      }
    },

    getCountryStyle(feature) {
      const geoCountryName = this.getCountryName(feature);
      const resolvedGeo = this.resolveCountryName(geoCountryName);

      const count = this.fellowCountryData[resolvedGeo] || 0;

      // Normalize both sides before matching
      const matchingKey = Object.keys(this.fellowCountryData).find(key =>
        this.resolveCountryName(key).toLowerCase() ===
        this.resolveCountryName(geoCountryName).toLowerCase()
      )

      const fellowCount = matchingKey ? this.fellowCountryData[matchingKey] : 0

      let fillColor = '#CCEBEF' // default for low/no data

      if (fellowCount >= 36) {
        fillColor = '#009CAD'
      } else if (fellowCount >= 21) {
        fillColor = '#57BEC9'
      } else if (fellowCount >= 10) {
        fillColor = '#92D4DC'
      }

      // Highlight when selected
      if (
        this.selectedCountryOnMap &&
        this.resolveCountryName(this.selectedCountryOnMap).toLowerCase() ===
        this.resolveCountryName(geoCountryName).toLowerCase()
      ) {
        return {
          fillColor: '#009CADff',
          weight: 2.5,
          opacity: 1,
          color: '#ffffff',
          fillOpacity: 0.9
        }
      }

      return {
        fillColor: fillColor,
        weight: 1.2,
        opacity: 0.8,
        color: '#ffffff',
        fillOpacity: 1
      }
    },

    toggleMapInteractivity(lock) {
      if (!this.geoJsonLayer) return;

      this.geoJsonLayer.eachLayer(layer => {
        if (lock) {
          // Disable click and hover interaction
          layer.off("click");
          layer.off("mouseover");
          layer.off("mouseout");

          // Remove cursor pointer
          layer.getElement()?.style?.setProperty("cursor", "default");
        } else {
          // Re-apply original handlers
          this.onEachFeature(layer.feature, layer);

          // Restore cursor for clickable countries
          layer.getElement()?.style?.setProperty("cursor", "pointer");
        }
      });
    },

    addCountryLabels() {
      if (!this.mapInstance) return;

      // Remove old markers
      this.countryMarkers.forEach(marker => marker.remove());
      this.countryMarkers = [];

      Object.entries(this.fellowCountryData).forEach(([country, count]) => {
        if (!count) return;

        const resolved = this.resolveCountryName(country);
        const coords = this.getCountryCoordinates(resolved);

        if (!coords) {
          console.warn(`⚠ No coordinates for "${country}" → resolved as "${resolved}"`);
          return;
        }

        const icon = L.divIcon({
          className: 'my-div-icon',
          html: `
            <div style="
              background: white;
              border: 2px solid #009CAD;
              border-radius: 50% 50% 50% 0;
              width: 30px;
              height: 30px;
              display: flex;
              align-items: center;
              justify-content: center;
              box-shadow: 0 3px 8px rgba(0,0,0,0.3);
              transform: rotate(-45deg);
            ">
              <div style="transform: rotate(45deg); font-weight:bold; color:#009CAD;">
                ${count}
              </div>
            </div>
          `,
          iconSize: [30, 30],
          iconAnchor: [10, 35]
        });

        const marker = L.marker(coords, { icon }).addTo(this.mapInstance)
        this.countryMarkers.push(marker)
      });
    },

    getCountryCoordinates(country) {
      if (!country) return null;

      // Always resolve/normalize first
      const resolved = this.resolveCountryName(country);

      const coords = {
        'South Africa': [-30.5595, 22.9375],
        'Kenya': [-0.0236, 37.9062],
        'Nigeria': [9.0820, 8.6753],
        'Ghana': [7.9465, -1.0232],
        'Tanzania': [-6.3690, 34.8888],
        'Egypt': [26.0975, 30.0444],
        'Morocco': [31.7917, -7.0926],
        'Uganda': [1.3733, 32.2903],
        'Ethiopia': [9.1450, 40.4897],
        'Zimbabwe': [-19.0154, 29.1549],
        'Zambia': [-13.1339, 27.8493],
        'Botswana': [-22.3285, 24.6849],
        'Namibia': [-22.9576, 18.4904],
        'Mali': [17.5707, -3.9962],
        'Burkina Faso': [12.2383, -1.5616],
        'Senegal': [14.4974, -14.4524],
        'Cameroon': [7.3697, 12.3547],
        'Madagascar': [-18.7669, 46.8691],
        'Angola': [-11.2027, 17.8739],
        'Mozambique': [-18.6657, 35.5296],

        'Democratic Republic of the Congo': [-4.0383, 21.7587],
        'Democratic Republic Of The Congo': [-4.0383, 21.7587],

        'Republic of the Congo': [-0.2280, 15.8277],
        'Republic Of The Congo': [-0.2280, 15.8277],

        'Algeria': [28.0339, 1.6596],
        'Tunisia': [33.8869, 9.5375],
        'Libya': [26.3351, 17.2283],
        'Sudan': [12.8628, 30.2176],
        'Chad': [15.4542, 18.7322],
        'Niger': [17.6078, 8.0817],
        'Mauritania': [21.0079, -10.9408],
        'Benin': [9.3077, 2.3158],
        'Togo': [8.6195, 0.8248],
        "Côte d'Ivoire": [7.5400, -5.5471],
        'Guinea': [9.9456, -9.6966],
        'Sierra Leone': [8.4606, -11.7799],
        'Liberia': [6.4281, -9.4295],
        'Gambia': [13.4432, -15.3101],
        'Guinea-Bissau': [11.8037, -15.1804],
        'Malawi': [-13.2543, 34.3015],
        'Rwanda': [-1.9403, 29.8739],
        'Burundi': [-3.3731, 29.9189],
        'Eritrea': [15.1794, 39.7823],
        'Djibouti': [11.8251, 42.5903],
        'Somalia': [5.1521, 46.1996],
        'Central African Republic': [6.6111, 20.9394],
        'Gabon': [-0.8037, 11.6094],
        'Equatorial Guinea': [1.6508, 10.2679],
        'Lesotho': [-29.6100, 28.2336],
        'Eswatini': [-26.5225, 31.4659]
      };

      // Try direct match on resolved name first
      if (coords[resolved]) return coords[resolved];

      // Fallback: try normalized title case as key
      const normalized = this.resolveCountryName(resolved);
      return coords[normalized] || null;
    },

    onEachFeature(feature, layer) {
      const countryName = this.getCountryName(feature)
      const matchingKey = Object.keys(this.fellowCountryData).find(key =>
        this.resolveCountryName(key).toLowerCase() ===
        this.resolveCountryName(countryName).toLowerCase()
      )

      const fellowCount = matchingKey ? this.fellowCountryData[matchingKey] : 0

      layer.bindPopup(`
    <div class="map-popup">
      <strong>${countryName}</strong><br>
      Fellows: <span class="badge bg-primary">${fellowCount}</span>
      ${fellowCount > 0 ? '<br><small class="text-muted">Click to filter fellows</small>' : ''}
    </div>
  `)

      layer.on({
        mouseover: (e) => {
          if (this.mapInteractionLocked) return;
          e.target.setStyle({
            weight: 2.5,
            color: '#666',
            fillOpacity: 1
          });
        },
        mouseout: (e) => {
          if (!this.isCountrySelected(countryName)) {
            this.geoJsonLayer.resetStyle(e.target)
          }
        },
        click: (e) => {
          // Prevent interaction if a filter is active
          if (this.mapInteractionLocked) {
            e.originalEvent.preventDefault();
            e.originalEvent.stopPropagation();
            return;
          }

          if (fellowCount > 0) {
            this.highlightCountry(countryName);
          }
        }
      })
    },

    getCountryName(feature) {
      const possibleNames = [
        feature.properties.NAME,
        feature.properties.NAME_EN,
        feature.properties.ADMIN,
        feature.properties.name
      ].filter(Boolean)
      return possibleNames[0] || 'Unknown Country'
    },

    highlightCountry(countryName) {
      // Prevent map selection if user filter is active
      if (this.mapInteractionLocked) {
        return;
      }

      if (
        this.selectedCountryOnMap &&
        this.selectedCountryOnMap.toLowerCase() === countryName.toLowerCase()
      ) {
        this.selectedCountryOnMap = null;
      } else {
        this.selectedCountryOnMap = countryName;
      }

      if (this.geoJsonLayer) {
        this.geoJsonLayer.eachLayer(layer => {
          layer.setStyle(this.getCountryStyle(layer.feature));
        });
      }

      this.displayLimit = 6;
    },

    showMapError() {
      const mapContainer = document.getElementById('africa-map')
      if (mapContainer) {
        mapContainer.innerHTML = `
<div class="text-center text-muted py-5">
<i class="bi bi-exclamation-triangle" style="font-size: 2rem;"></i>
<p class="mt-2">Error loading map</p>
<small>Please check your internet connection</small>
</div>
`
      }
    },

    async loadFellows() {
      this.loading = true;
      try {
        const response = await UserService.getUsersWithFellowshipData({ per_page: 1000 });

        let fellows = [];

        if (response?.Data?.data) {
          fellows = response.Data.data;
        } else if (response?.data?.data) {
          fellows = response.data.data;
        } else if (Array.isArray(response)) {
          fellows = response;
        }

        // ============================================================
        // ⭐ NORMALIZE EACH FELLOW'S DATA
        // ============================================================
        this.allFellows = fellows.map(f => {

          // ================================================
          // ⭐ 1. NORMALIZE FELLOWSHIP CALLS (MULTIPLE RECORDS)
          // ================================================
          let fellowshipCalls = [];

          if (Array.isArray(f.fellowship_details)) {
            fellowshipCalls = f.fellowship_details
              .map(fd => Number(fd.edctp_project_call))
              .filter(v => !isNaN(v));
          }

          const normalized_fellowship_calls = [...new Set(fellowshipCalls)];

          // ================================================
          // ⭐ 2. NORMALIZE RESEARCH AREAS
          // ================================================
          const diseaseAreas = [];

          if (Array.isArray(f.fellowship_details)) {
            f.fellowship_details.forEach(fd => {
              const code = Number(fd.disease_area);
              if (DISEASE_AREA_MAP[code]) {
                diseaseAreas.push(DISEASE_AREA_MAP[code]);
              }
            });
          }

          const normalized_research_areas = [...new Set(diseaseAreas)];

          // ================================================
          // ⭐ RETURN NORMALIZED FELLOW OBJECT
          // ================================================
          return {
            ...f,
            full_name: f.name,

            // FIX: ensure country always exists
            country: f.country ?? f.computed_country ?? null,

            normalized_fellowship_calls,
            normalized_research_areas
          };
        });

      } catch (error) {
        console.error("Load fellows error:", error);
        this.showToast("error", "Failed to load fellows");
        this.allFellows = [];
      } finally {
        this.loading = false;
      }
    },

    getFellowshipYear(fellow) {
      if (fellow.fellowship_year) return fellow.fellowship_year

      if (fellow.fellowship_details) {
        const startDate = fellow.fellowship_details.edctp_project_start_date ||
          fellow.fellowship_details.start_date
        if (startDate) {
          return new Date(startDate).getFullYear()
        }

        const callDate = fellow.fellowship_details.edctp_call_date
        if (callDate) {
          return new Date(callDate).getFullYear()
        }
      }
      return null
    },

    sortFellowsList(fellows) {
      switch (this.sortBy) {
        case 'name':
          return fellows.sort((a, b) => a.full_name.localeCompare(b.full_name))
        case 'year':
          return fellows.sort((a, b) => (b.fellowship_year || 0) - (a.fellowship_year || 0))
        case 'country':
          return fellows.sort((a, b) => (a.country || '').localeCompare(b.country || ''))
        case 'updated':
        default:
          return fellows.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
      }
    },

    performSearch() {
      this.displayLimit = 6
      this.selectedCountryOnMap = null
    },

    applyFilters() {
      this.displayLimit = 6;

      const lock = !!this.selectedLocation;
      this.mapInteractionLocked = lock;

      this.disableAllClicks(lock);

      const el = document.getElementById("africa-map");
      if (el) el.classList.toggle("map-disabled", lock);

      this.selectedCountryOnMap = null;
      this.generateCountryData(this.filteredFellows);

      if (!this.selectedLocation) {
        this.addCountryLabels();
        this.mapInstance.setView([0, 20], 3, { animate: false });
      } else {
        this.updateMarkersForSelectedCountry(this.selectedLocation);
      }

      // ---- FIXED NULL CHECK ----
      if (this.geoJsonLayer) {
        this.geoJsonLayer.eachLayer(layer =>
          layer.setStyle(this.getCountryStyle(layer.feature))
        );
      }
    },

    sortFellows() {
// Sorting handled in computed property
    },

    loadMoreFellows() {
      this.displayLimit += 6
    },

    disableAllClicks(lock) {
      if (!this.mapInstance || !this.geoJsonLayer) return;

      if (lock) {
        this.mapInstance.dragging.disable();
        this.mapInstance.scrollWheelZoom.disable();
        this.mapInstance.doubleClickZoom.disable();
        this.mapInstance.boxZoom.disable();
        this.mapInstance.keyboard.disable();
        this.mapInstance.touchZoom && this.mapInstance.touchZoom.disable();
        this.mapInstance.tap && this.mapInstance.tap.disable();

        // Prevent popups & clicks on all layers
        this.geoJsonLayer.eachLayer(layer => {
          layer.off("click");
          layer.off("mouseover");
          layer.off("mouseout");
        });

        // Block pointer events (markers too)
        this.mapInstance.getContainer().style.pointerEvents = "none";

      } else {
        this.mapInstance.dragging.enable();
        this.mapInstance.scrollWheelZoom.enable();
        this.mapInstance.doubleClickZoom.enable();
        this.mapInstance.boxZoom.enable();
        this.mapInstance.keyboard.enable();
        this.mapInstance.touchZoom && this.mapInstance.touchZoom.enable();
        this.mapInstance.tap && this.mapInstance.tap.enable();

        // Restore interactivity
        this.geoJsonLayer.eachLayer((layer) => {
          this.onEachFeature(layer.feature, layer);
        });

        // Re-enable mouse interaction
        this.mapInstance.getContainer().style.pointerEvents = "auto";
      }
    },

    clearFilters() {
      this.searchQuery = "";
      this.selectedLocation = "";
      this.selectedFellowship = "";
      this.selectedFellowshipType = "";
      this.selectedWorkingGroup = "";
      this.selectedCountryOnMap = null;
      this.displayLimit = 6;

      this.generateCountryData(this.filteredFellows);

      this.mapInteractionLocked = false;
      this.toggleMapInteractivity(false);
      this.disableAllClicks(false);

      this.addCountryLabels();
      if (this.geoJsonLayer) {
        this.geoJsonLayer.eachLayer(layer =>
          layer.setStyle(this.getCountryStyle(layer.feature))
        );
      }
      this.mapInstance.setView([0, 20], 3);
    },

    openFellowModal(fellow) {
      this.selectedFellow = fellow;
      this.isFellowPreviewOpen = true;
    },
    closeFellowModal() {
      this.isFellowPreviewOpen = false
      this.selectedFellow = null
    }
  }
}
</script>

<style scoped>
.map-disabled {
  opacity: 0.45;
  pointer-events: none;
  transition: opacity 0.3s ease;
}

/* Publications Section */
.publications-section {
  background: white;
  padding: 1.5rem;
  border-radius: 12px;
  border: 1px solid #e9ecef;
}

.publications-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.publication-item {
  border: 1px solid #dee2e6;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.3s ease;
}

.publication-item:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.publication-header {
  padding: 1rem;
  background: #f8f9fa;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.2s ease;
}

.publication-header:hover {
  background: #e9ecef;
}

.publication-header.expanded {
  background: #e3f2fd;
  border-bottom: 1px solid #bbdefb;
}

.pub-title-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
}

.toggle-icon {
  transition: transform 0.3s ease;
  color: #6c757d;
  font-size: 0.9rem;
  flex-shrink: 0;
}

.toggle-icon.rotated {
  transform: rotate(90deg);
  color: #1976d2;
}

.pub-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: #2c3e50;
  line-height: 1.4;
}

.pub-date {
  color: #6c757d;
  font-size: 0.85rem;
  white-space: nowrap;
  margin-left: 1rem;
}

.publication-details {
  padding: 1.5rem;
  background: white;
  animation: slideDown 0.3s ease;
}

@keyframes slideDown {
  from {
    opacity: 0;
    max-height: 0;
  }
  to {
    opacity: 1;
    max-height: 2000px;
  }
}

.pub-detail-section {
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #f1f3f4;
}

.pub-detail-section:last-child {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
}

.pub-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #495057;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: block;
  margin-bottom: 0.5rem;
}

.pub-text {
  color: #2c3e50;
  font-size: 0.9rem;
  line-height: 1.5;
}

.pub-content {
  background: #f8f9fa;
  padding: 0.75rem;
  border-radius: 6px;
  border-left: 3px solid #1976d2;
  color: #2c3e50;
  font-size: 0.9rem;
  line-height: 1.6;
}

.authors-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  padding: 0.4rem 0;
  user-select: none;
}

.authors-header:hover .pub-label {
  color: #1976d2;
}

.authors-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.author-tag {
  background: #e3f2fd;
  color: #1976d2;
  padding: 0.4rem 0.8rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 500;
  border: 1px solid #bbdefb;
  display: inline-flex;
  align-items: center;
}

.external-links {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.external-link {
  display: inline-flex;
  align-items: center;
  color: #1976d2;
  text-decoration: none;
  font-weight: 500;
  font-size: 0.9rem;
  padding: 0.5rem 0.75rem;
  background: #f8f9fa;
  border-radius: 6px;
  transition: all 0.2s ease;
  width: fit-content;
}

.external-link:hover {
  background: #e3f2fd;
  transform: translateX(4px);
}

.external-link i {
  font-size: 1rem;
}

/* Status Badge Variants */
.status-badge-large {
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  display: inline-block;
}

.status-active {
  background: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.status-completed {
  background: #cce5ff;
  color: #0056b3;
  border: 1px solid #b3d7ff;
}

.status-suspended {
  background: #f8d7da;
  color: #721c24;
  border: 1px solid #f1b2b7;
}

.status-pending {
  background: #fff3cd;
  color: #856404;
  border: 1px solid #ffeaa7;
}

.status-default {
  background: #e9ecef;
  color: #495057;
  border: 1px solid #ced4da;
}

/* Sites Tags */
.sites-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.site-tag {
  background: #e3f2fd;
  color: #1976d2;
  padding: 0.4rem 0.8rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 500;
  border: 1px solid #bbdefb;
  display: inline-flex;
  align-items: center;
}

/* Collaborators List */
.collaborators-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.collaborator-item {
  display: flex;
  align-items: center;
  padding: 0.75rem;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 3px solid #1a8090;
}

.collaborator-item i {
  color: #1a8090;
  font-size: 1rem;
}

.collab-text {
  font-size: 0.9rem;
  font-weight: 500;
  color: #2c3e50;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1050;
  padding: 1rem;
}

.modal-dialog {
  width: 100%;
  max-width: 900px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-content {
  border: none;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.2);
  background: white;
}

.modal-header {
  background: linear-gradient(135deg, #1a8090 0%, #26d0ce 100%);
  color: white;
  border: none;
  padding: 1.5rem;
  position: relative;
}

.modal-header-content {
  display: flex;
  align-items: center;
  gap: 1rem;
  width: 100%;
}

.modal-avatar {
  position: relative;
  flex-shrink: 0;
}

.modal-avatar-img {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid rgba(255, 255, 255, 0.3);
}

.modal-status-badge {
  position: absolute;
  top: -2px;
  right: -2px;
  width: 16px;
  height: 16px;
  background: #28a745;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
}

.modal-title-section {
  flex: 1;
}

.modal-title {
  font-size: 1.4rem;
  font-weight: 700;
  margin-bottom: 0.25rem;
  color: white;
}

.modal-subtitle {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
}

.btn-close {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.25rem;
  line-height: 1;
  opacity: 0.8;
}

.btn-close:hover {
  opacity: 1;
}

.modal-body {
  padding: 1.5rem;
  background: #f8f9fa;
}

.tab-navigation {
  border-bottom: 2px solid #dee2e6;
}

.nav-tabs {
  border-bottom: none;
  margin-bottom: 0;
}

.nav-tabs .nav-link {
  border: 1px solid transparent;
  border-top-left-radius: 0.375rem;
  border-top-right-radius: 0.375rem;
  background-color: transparent;
  color: #6c757d;
  padding: 0.75rem 1.25rem;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.3s ease;
  cursor: pointer;
}

.nav-tabs .nav-link:hover {
  border-color: #e9ecef #e9ecef #dee2e6;
  background-color: #f8f9fa;
  color: #495057;
}

.nav-tabs .nav-link.active {
  color: #1a8090;
  background-color: #fff;
  border-color: #dee2e6 #dee2e6 #fff;
  font-weight: 600;
}

/* Info Boxes */
.info-box {
  background: white;
  padding: 1rem;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.info-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.5rem;
  display: block;
}

.info-text {
  font-size: 0.95rem;
  font-weight: 500;
  color: #2c3e50;
  line-height: 1.4;
}

/* Fellowship Sections */
.fellowship-section {
  background: white;
  padding: 1.5rem;
  border-radius: 12px;
  border: 1px solid #e9ecef;
}

.section-header {
  color: #495057;
  font-weight: 600;
  font-size: 1rem;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #e9ecef;
}

.section-header i {
  color: #1a8090;
}

.status-badge-large {
  background: #e9ecef;
  color: #495057;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  display: inline-block;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 3rem 1rem;
  color: #6c757d;
}

.empty-state i {
  font-size: 3rem;
  color: #dee2e6;
  margin-bottom: 1rem;
}

.modal-footer {
  background: white;
  border: none;
  padding: 1rem 1.5rem;
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
}

.modal-footer .btn {
  padding: 0.5rem 1.25rem;
  border-radius: 8px;
  font-size: 0.9rem;
}

.btn-secondary {
  background-color: #6c757d;
  border-color: #6c757d;
  color: white;
}

/* Responsive */
@media (max-width: 768px) {
  .modal-dialog {
    max-height: 95vh;
  }

  .modal-header-content {
    flex-direction: column;
    text-align: center;
  }

  .modal-body {
    padding: 1rem;
  }
}
/* Map Marker Styles - Pin Shape */
.leaflet-marker-icon.my-div-icon {
  background-color: #FFFFFF !important;
  border: 2px solid #009CAD !important;
  border-radius: 50% 50% 50% 0 !important;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.3) !important;
  width: 30px !important;
  height: 30px !important;
  transform: rotate(-45deg) !important;
}

.my-div-icon .d-flex {
  width: 100%;
  height: 100%;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}

.my-div-icon .marker-text-div {
  transform: rotate(45deg);
  color: #009CAD;
  font-weight: bold;
  font-size: 15px;
  line-height: 1;
}

/* Map Container */
#africa-map {
  border-radius: 12px;
  background: #FFFFFF !important;
}

.leaflet-container {
  background: #FFFFFF !important;
}

.map-container {
  background: #fff;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 4px 16px rgba(13, 115, 119, 0.1);
  border: 1px solid #e0f2f3;
}

/* Popup Styling */
.leaflet-popup-content-wrapper {
  border-radius: 10px;
  box-shadow: 0 4px 15px rgba(13, 115, 119, 0.2);
  border: 2px solid #80ede9;
}

.map-popup strong {
  color: #0d7377;
  font-size: 1.1rem;
  font-weight: 700;
  display: block;
  margin-bottom: 0.5rem;
}

.map-popup .badge {
  background: #0d7377 !important;
  color: white;
  padding: 4px 10px;
  border-radius: 12px;
  font-weight: 600;
}

/* Hide Attribution */
.leaflet-control-attribution {
  display: none !important;
}

/* Zoom Controls */
.leaflet-control-zoom {
  border: 2px solid #0d7377 !important;
  border-radius: 8px !important;
  overflow: hidden;
}

.leaflet-control-zoom a {
  background: white !important;
  color: #0d7377 !important;
}

.leaflet-control-zoom a:hover {
  background: #0d7377 !important;
  color: white !important;
}

/* Search Section */
.search-section {
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

.search-input-container {
  position: relative;
  min-width: 250px;
}

.search-icon {
  position: absolute;
  left: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
  z-index: 2;
}

.search-input {
  padding: 12px 15px 12px 45px;
  border: 2px solid #e9ecef;
  border-radius: 25px;
  background-color: white;
}

.search-input:focus {
  border-color: #1a8090;
  box-shadow: 0 0 0 0.2rem rgba(26, 128, 144, 0.25);
  outline: none;
}

.filter-container {
  position: relative;
  min-width: 180px;
}

.filter-icon {
  position: absolute;
  left: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
  z-index: 2;
  pointer-events: none;
}

.filter-select {
  padding: 12px 15px 12px 45px;
  border: 2px solid #e9ecef;
  border-radius: 25px;
  background-color: white;
  cursor: pointer;
}

.search-btn {
  padding: 12px 30px;
  border-radius: 25px;
  font-weight: 600;
  border: none;
}

/* Fellows List */
.fellows-list-container {
  height: 39rem;
  overflow-y: auto;
  padding: 1rem;
  background: #fff;
  border-radius: 15px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
  border: 1px solid #e9ecef;
}

.results-header {
  border-bottom: 2px solid #dee2e6;
  padding: 1rem;
  margin: 0 -1rem 1.5rem -1rem;
}

.count-number {
  font-size: 2rem;
  font-weight: 700;
  color: #1a8090;
  margin: 0 0.5rem;
}

/* Fellow Cards */
.fellow-card-compact {
  background: white;
  border-radius: 12px;
  padding: 1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid #e9ecef;
  transition: all 0.3s ease;
  margin-bottom: 1rem;
  position: relative;
}

.fellow-card-compact::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(135deg, #1a8090 0%, #26d0ce 100%);
  border-radius: 12px 12px 0 0;
}

.fellow-card-compact:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(26, 128, 144, 0.15);
}

.fellow-card-compact.highlighted {
  box-shadow: 0 0 0 2px rgba(0, 156, 173, 0.2);
}

.avatar-img-compact {
  width: 55px;
  height: 55px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #fff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
}

.view-btn-compact {
  width: 100%;
  padding: 0.75rem 1rem;
  font-size: 0.85rem;
  font-weight: 600;
  border: none;
  border-radius: 8px;
  background: #1a8090;
  color: white;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.view-btn-compact:hover {
  background: #0d5d6b;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(26, 128, 144, 0.25);
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1050;
  padding: 1rem;
}

.modal-content {
  border: none;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.2);
  background: white;
}

.modal-header {
  background: linear-gradient(135deg, #1a8090 0%, #26d0ce 100%);
  color: white;
  padding: 1.5rem;
}

/* Responsive */
@media (max-width: 768px) {
  #africa-map {
    height: 300px !important;
  }

  .fellows-list-container {
    height: auto;
    max-height: 600px;
  }
}
</style>
